// ════════════════════════════════════════════════════════════════════════════
//  Udisys Gestor Web  —  src/features/configuracoes/hooks/useConfigNfse.ts
// ════════════════════════════════════════════════════════════════════════════

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { getApiErrorMessage } from '@/lib/apiClient';
import {
  configNfseService,
  type SalvarConfigNfseRequest,
} from '../services/configNfseService';

const KEY = (empresaId: number) => ['config', 'nfse', empresaId] as const;

export const useConfigNfse = (empresaId: number) => {
  const qc = useQueryClient();

  const query = useQuery({
    queryKey: KEY(empresaId),
    queryFn:  () => configNfseService.obter(empresaId),
    enabled:  empresaId > 0,
  });

  const salvar = useMutation({
    mutationFn: (req: SalvarConfigNfseRequest) =>
      configNfseService.salvar(empresaId, req),
    onSuccess: (data) => {
      qc.setQueryData(KEY(empresaId), data);
      toast.success('Configuração NFS-e salva');
    },
    onError: (e) => toast.error(getApiErrorMessage(e)),
  });

  const validar = useMutation({
    mutationFn: () => configNfseService.validarCertificado(empresaId),
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: KEY(empresaId) });
      if (res.sucesso) toast.success(res.mensagem ?? 'Certificado válido');
      else             toast.error(res.mensagem ?? 'Falha na validação do certificado');
    },
    onError: (e) => toast.error(getApiErrorMessage(e)),
  });

  return {
    config:        query.data ?? null,
    isLoading:     query.isLoading,
    salvar:        salvar.mutate,
    isSaving:      salvar.isPending,
    validarCert:   validar.mutate,
    isValidating:  validar.isPending,
    validacaoData: validar.data,
  };
};
